/**
 * PresentationsPage — deck performance area/negozi (AM: propri store, OPS: tutti).
 * Lista presentazioni create + creazione con mese/anno e selezione negozi.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Presentation, Plus, Loader2, Inbox, ExternalLink, Link2, FileDown, Trash2 } from 'lucide-react';
import NewPresentationDialog from '../components/NewPresentationDialog';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function PresentationsPage() {
  const [items, setItems] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [busyPdf, setBusyPdf] = useState(null);

  const load = useCallback(() => {
    api.get('/presentations')
      .then(r => setItems(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || 'Errore caricamento'); setItems([]); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const publicUrl = (p) => `${API_BASE}/api/presentations/public/${p.share_token}`;

  const copyLink = async (p) => {
    try {
      await navigator.clipboard.writeText(publicUrl(p));
      toast.success('Link copiato — condivisibile senza login');
    } catch {
      toast.error('Copia non riuscita');
    }
  };

  const downloadPdf = async (p) => {
    setBusyPdf(p.presentation_id);
    try {
      const r = await api.get(`/presentations/${p.presentation_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${p.title.replaceAll(' ', '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download PDF fallito');
    } finally {
      setBusyPdf(null);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/presentations/${toDelete.presentation_id}`);
      toast.success('Presentazione eliminata');
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eliminazione fallita');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1920px] space-y-6" data-testid="presentations-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Presentation className="w-6 h-6" /> Presentazioni
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Deck performance dell'area o dei negozi selezionati, nello stile aziendale — con link condivisibile senza login.
          </p>
        </div>
        <Button onClick={() => setShowNew(true)} className="gap-1.5" data-testid="new-presentation-btn">
          <Plus className="w-4 h-4" /> Nuova presentazione
        </Button>
      </div>

      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-300 py-16 text-center text-slate-500" data-testid="presentations-empty">
          <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">Nessuna presentazione ancora. Creane una con i dati del periodo (mese o trimestre)!</p>
        </div>
      ) : (
        <div className="border border-slate-200 divide-y divide-slate-100 bg-white" data-testid="presentations-list">
          {items.map(p => (
            <div key={p.presentation_id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3"
                 data-testid={`presentation-row-${p.presentation_id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 truncate">{p.title}</p>
                <p className="text-xs text-slate-500">
                  {p.period_label ? `${p.period_label} · ` : ''}{p.stores_count} negozi · creato da {p.created_by_name} il {fmtDate(p.created_at)}
                  {p.ai_comment && <span className="ml-2 text-indigo-600 font-medium">✦ con analisi AI</span>}
                </p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <a href={publicUrl(p)} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium border border-slate-300 hover:border-slate-500 text-slate-700"
                   data-testid={`presentation-open-${p.presentation_id}`}>
                  <ExternalLink className="w-3.5 h-3.5" /> Apri
                </a>
                <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => copyLink(p)}
                        data-testid={`presentation-copy-${p.presentation_id}`}>
                  <Link2 className="w-3.5 h-3.5" /> Copia link
                </Button>
                <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => downloadPdf(p)}
                        disabled={busyPdf === p.presentation_id} data-testid={`presentation-pdf-${p.presentation_id}`}>
                  {busyPdf === p.presentation_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600"
                        onClick={() => setToDelete(p)} data-testid={`presentation-delete-${p.presentation_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <NewPresentationDialog open={showNew} onClose={() => setShowNew(false)}
                             onCreated={() => { setShowNew(false); load(); }} />

      <AlertDialog open={!!toDelete} onOpenChange={v => { if (!v) setToDelete(null); }}>
        <AlertDialogContent data-testid="presentation-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare "{toDelete?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>Il link pubblico smetterà di funzionare.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700"
                               data-testid="presentation-delete-confirm-btn">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
